import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from './../../components/card/card.jsx';
import rtoService from '../../services/rtoService';
import toastService from '../../services/toastService';
import authService from '../../services/authService';

const currencyOptions = ['USD', 'EUR', 'GBP'];

const ModelsPricing = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(true);
  const [models, setModels] = useState([]);
  const [error, setError] = useState('');

  const normalizeModels = (raw) => {
    const arr = Array.isArray(raw?.models) ? raw.models : Array.isArray(raw) ? raw : [];
    return arr.map((m, idx) => ({
      id: m.id || idx,
      provider: m.provider || '',
      model: m.model || m.name || '',
      price_per_1k: (m.price_per_1k ?? 0) * 1,
      enabled: (m.enabled ?? true) === true,
    }));
  };

  const fetchModels = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await rtoService.getAIModels();
      setModels(normalizeModels(data));
      setAvailable(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        setAvailable(false);
        setError('Models & Pricing endpoint is not available on the server yet.');
      } else {
        setError(e.message || 'Failed to load models');
        toastService.error(e.message || 'Failed to load models');
      }
    } finally {
      setLoading(false);
    }
  };

  const addModel = () => {
    setModels([
      ...models,
      { id: `tmp-${Date.now()}`, provider: '', model: '', price_per_1k: 0, enabled: true },
    ]);
  };

  const removeModel = (idx) => {
    const next = models.slice();
    next.splice(idx, 1);
    setModels(next);
  };

  const updateField = (idx, key, value) => {
    const next = models.slice();
    next[idx] = { ...next[idx], [key]: value };
    setModels(next);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const invalid = models.find((m) => !m.provider || !m.model);
      if (invalid) {
        const msg = 'Please fill provider and model for all entries';
        setError(msg);
        toastService.error(msg);
        setSaving(false);
        return;
      }
      const payload = models.map(({ provider, model, price_per_1k, enabled }) => ({
        provider,
        model,
        price_per_1k: Number(price_per_1k) || 0,
        enabled: !!enabled,
      }));
      const data = await rtoService.updateAIModels(payload);
      setModels(normalizeModels(data));
      toastService.success('Models & Pricing updated');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to update models';
      setError(msg);
      toastService.error(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">AI Models & Pricing</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchModels} disabled={loading}>Refresh</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || loading || !available}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>

      {!available && (
        <div className="alert alert-warning" role="alert">
          Models & Pricing endpoints are not yet available on the server. This UI will work once backend is implemented.
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <Card>
        <CardHeader>
          <div className="fw-semibold">Configure available AI models and price per 1k tokens</div>
          <div className="text-muted small">These values are used for calculating AI usage costs in analytics.</div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="d-flex align-items-center"><div className="spinner-border me-2" role="status" /> Loading…</div>
          ) : (
            <>
              <div className="mb-3 text-end">
                <button className="btn btn-sm btn-outline-primary" onClick={addModel}>Add Model</button>
              </div>
              {models.length === 0 ? (
                <div className="text-muted small">No models yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{minWidth: 160}}>Provider</th>
                        <th style={{minWidth: 200}}>Model</th>
                        <th style={{minWidth: 180}}>Price $/1k tokens</th>
                        <th style={{minWidth: 120}}>Enabled</th>
                        <th style={{width: 80}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {models.map((m, idx) => (
                        <tr key={m.id || idx}>
                          <td>
                            <input className="form-control" value={m.provider} onChange={(e) => updateField(idx, 'provider', e.target.value)} placeholder="e.g. openai" />
                          </td>
                          <td>
                            <input className="form-control" value={m.model} onChange={(e) => updateField(idx, 'model', e.target.value)} placeholder="e.g. gpt-4o-mini" />
                          </td>
                          <td>
                            <input type="number" step="0.0001" className="form-control" value={m.price_per_1k}
                              onChange={(e) => updateField(idx, 'price_per_1k', e.target.value)} placeholder="e.g. 0.015" />
                          </td>
                          <td>
                            <div className="form-check form-switch">
                              <input className="form-check-input" type="checkbox" checked={!!m.enabled} onChange={(e) => updateField(idx, 'enabled', e.target.checked)} />
                            </div>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeModel(idx)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const OrgModelMapping = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState('');
  const [mapping, setMapping] = useState({});
  const [models, setModels] = useState([]);
  const [orgs, setOrgs] = useState([]);

  const normalizeModels = (raw) => {
    const arr = Array.isArray(raw?.models) ? raw.models : Array.isArray(raw) ? raw : [];
    return arr.map((m, idx) => ({ id: m.id || idx, provider: m.provider || '', model: m.model || '' }));
  };

  // Normalize organizations to consistent shape: { id, name, active }
  const normalizeOrgs = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .map((o, idx) => {
        if (typeof o === 'string') {
          const name = o.trim();
          return name ? { id: name, name, active: true } : null;
        }
        const name = String(o?.name ?? o?.organisation ?? o?.organization ?? o?.org ?? '').trim();
        if (!name) return null;
        const id = o?.id ?? idx;
        const active = o?.active !== false;
        return { id, name, active };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [modelsData, mappingData, orgsData] = await Promise.all([
        rtoService.getAIModels().catch((e) => {
          if (e?.response?.status === 404) return { models: [] };
          throw e;
        }),
        rtoService.getOrgModelMapping(),
        authService.getOrganizations().catch(() => []),
      ]);
      setModels(normalizeModels(modelsData));
      const rawMap = mappingData?.mapping || mappingData || {};
      const sanitized = {};
      if (rawMap && typeof rawMap === 'object') {
        Object.entries(rawMap).forEach(([org, conf]) => {
          if (conf && typeof conf === 'object') {
            const provider = String(conf.provider || '').trim();
            const model = String(conf.model || '').trim();
            sanitized[org] = { provider, model };
          }
        });
      }
      setMapping(sanitized);

      // normalize orgs
      const orgList = normalizeOrgs(orgsData);
      setOrgs(orgList);

      setAvailable(true);
    } catch (e) {
      if (e?.response?.status === 404) {
        setAvailable(false);
        setError('Org mapping endpoint is not available on the server yet.');
      } else {
        setError(e.message || 'Failed to load mapping');
        toastService.error(e.message || 'Failed to load mapping');
      }
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const bad = Object.keys(mapping).find((k) => !k || !k.trim());
      if (bad !== undefined) {
        const msg = 'Organization name cannot be empty';
        setError(msg);
        toastService.error(msg);
        setSaving(false);
        return;
      }
      // Validate each conf has provider and model
      const missing = Object.entries(mapping).find(([_, conf]) => !conf || !conf.provider || !conf.model);
      if (missing) {
        const msg = 'Each organization must have a provider: model selected';
        setError(msg);
        toastService.error(msg);
        setSaving(false);
        return;
      }
      const data = await rtoService.updateOrgModelMapping(mapping);
      const rawMap = data?.mapping || data || {};
      const sanitized = {};
      if (rawMap && typeof rawMap === 'object') {
        Object.entries(rawMap).forEach(([org, conf]) => {
          if (conf && typeof conf === 'object') {
            sanitized[org] = { provider: String(conf.provider || ''), model: String(conf.model || '') };
          }
        });
      }
      setMapping(sanitized);
      toastService.success('Org → Model mapping updated');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to update mapping';
      setError(msg);
      toastService.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    const used = new Set(Object.keys(mapping));
    const firstUnused = (orgs || []).find((o) => o && o.active !== false && !used.has(o.name));
    const newKey = firstUnused ? firstUnused.name : '';
    setMapping({ ...mapping, [newKey]: { provider: '', model: '' } });
  };

  const removeRow = (org) => {
    const next = { ...mapping };
    delete next[org];
    setMapping(next);
  };

  const changeOrgKey = (oldOrg, newOrg) => {
    if (oldOrg === newOrg) return;
    if (newOrg && Object.prototype.hasOwnProperty.call(mapping, newOrg)) {
      toastService.error('Организация уже есть в списке сопоставления');
      return;
    }
    const next = { ...mapping };
    const val = next[oldOrg];
    delete next[oldOrg];
    next[newOrg] = val && typeof val === 'object' ? val : { provider: '', model: '' };
    setMapping(next);
  };

  const changeOrgModel = (org, value) => {
    if (!value) {
      setMapping({ ...mapping, [org]: { provider: '', model: '' } });
      return;
    }
    const [provider, model] = String(value).split('::');
    setMapping({ ...mapping, [org]: { provider: provider || '', model: model || '' } });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Provide options for select from models
  const modelOptions = models.map((m) => ({
    value: `${m.provider}::${m.model}`,
    label: `${m.provider} / ${m.model}`,
  }));

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h4 mb-0">Per-Organization Model Assignment</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchAll} disabled={loading}>Refresh</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || loading || !available}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>

      {!available && (
        <div className="alert alert-warning" role="alert">
          Org mapping endpoints are not yet available on the server. This UI will work once backend is implemented.
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <Card>
        <CardHeader>
          <div className="fw-semibold">Map organizations to default AI models</div>
          <div className="text-muted small">If an organization is not listed here, a global default may apply on the server side.</div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="d-flex align-items-center"><div className="spinner-border me-2" role="status" /> Loading…</div>
          ) : (
            <>
              <div className="mb-3 text-end">
                <button className="btn btn-sm btn-outline-primary" onClick={addRow}>Add Organization</button>
              </div>
              {Object.keys(mapping).length === 0 ? (
                <div className="text-muted small">No organization mappings yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{minWidth: 220}}>Organization</th>
                        <th style={{minWidth: 260}}>Model</th>
                        <th style={{width: 80}}></th>
                      </tr>
                    </thead>
                  <tbody>
                    {Object.entries(mapping).map(([org, conf]) => (
                      <tr key={org + '::' + (conf?.provider || '') + '::' + (conf?.model || '')}>
                        <td>
                          {/* Replace text input with dropdown of active orgs, falling back to text if list empty */}
                          {orgs && orgs.length > 0 ? (
                              (() => {
                                const used = new Set(Object.keys(mapping));
                                const options = (orgs || [])
                                  .filter((o) => o && o.active !== false && (o.name === org || !used.has(o.name)));
                                return (
                                  <select
                                    className="form-select"
                                    value={org}
                                    onChange={(e) => changeOrgKey(org, e.target.value)}
                                  >
                                    <option value="">— Select organization —</option>
                                    {options.map((o) => (
                                      <option key={o.id} value={o.name}>{o.name}</option>
                                    ))}
                                  </select>
                                );
                              })()
                          ) : (
                            <input className="form-control" value={org} onChange={(e) => changeOrgKey(org, e.target.value)} placeholder="e.g. ACME Corp" />
                          )}
                        </td>
                        <td>
                          <select
                              className="form-select"
                              value={conf && conf.provider && conf.model ? `${conf.provider}::${conf.model}` : ''}
                              onChange={(e) => changeOrgModel(org, e.target.value)}
                            >
                              <option value="">— Select model —</option>
                              {modelOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                            </select>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeRow(org)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const AdminSettings = () => {
  const [tab, setTab] = useState('models');

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h2 mb-0">Settings</h1>
      </div>

      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'models' ? 'active' : ''}`} onClick={() => setTab('models')}>Models & Pricing</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'mapping' ? 'active' : ''}`} onClick={() => setTab('mapping')}>Org → Model Mapping</button>
        </li>
      </ul>

      <div className="tab-content">
        <div className={`tab-pane fade ${tab === 'models' ? 'show active' : ''}`}>
          <ModelsPricing />
        </div>
        <div className={`tab-pane fade ${tab === 'mapping' ? 'show active' : ''}`}>
          <OrgModelMapping />
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;